import { readFileSync, writeFileSync, mkdirSync, existsSync } from 'fs';
import { join } from 'path';
import { parse as parseYaml, stringify as stringifyYaml } from 'yaml';
import { CloudGridApiClient } from './api/legacy-client.js';
import { readCredentials } from './credentials.js';
import { getActiveOrgSlug } from './active-org.js';
import { CloudGridError } from '../../shared/src/errors.js';
export { getCloudGridHome } from './home.js';
import { getCloudGridHome } from './home.js';

/**
 * Canonical production API base. The single default seeded into a fresh or
 * partial `~/.cloudgrid/config.yaml`. Kept as a named export so the login
 * self-heal, `detectConfig`, and `doctor --fix` all agree on one value.
 */
export const PROD_API_URL = 'https://api.cloudgrid.io';

export interface CloudGridConfig {
  api_url: string;
  api_key?: string;
  registry: string;
  domain: string;
  /** Cloudflare zone ID used by the legacy DNS commands (dns.ts). */
  cloudflare_zone_id?: string;
  /**
   * @deprecated Reads from this field for backwards compat with v0.7.x
   * configs; v0.8 stores JWTs in the separate `~/.cloudgrid/credentials`
   * file (mode 0600). Always undefined for v0.8-issued configs.
   */
  jwt?: string;
  /**
   * Backlog 072-C — gate for OS-level desktop notifications on plug
   * terminal state. Defaults to `true` when missing, so existing
   * configs opt in automatically. Users override by setting `false`
   * here or by passing `--no-notify` on the plug verb.
   */
  notify_on_deploy?: boolean;
}

export function getConfigDir() { return getCloudGridHome(); }
export function getAppsDir() { return join(getCloudGridHome(), 'apps'); }
export function configExists() { return existsSync(join(getCloudGridHome(), 'config.yaml')); }

export function loadConfig(): CloudGridConfig {
  const configPath = join(getCloudGridHome(), 'config.yaml');
  if (!existsSync(configPath)) {
    throw new Error('No CLI config found at ~/.cloudgrid/config.yaml. Run: grid login');
  }
  return parseYaml(readFileSync(configPath, 'utf-8'));
}

export function saveConfig(config: CloudGridConfig) {
  const configDir = getCloudGridHome();
  mkdirSync(configDir, { recursive: true });
  writeFileSync(join(configDir, 'config.yaml'), stringifyYaml(config));
}

export function detectConfig(): Partial<CloudGridConfig> {
  return {
    api_url: PROD_API_URL,
    domain: 'cloudgrid.io',
  };
}

/**
 * Read the raw on-disk config as a plain object, preserving every key
 * (including ones not modelled on `CloudGridConfig`, e.g. `active_org_slug`,
 * `telemetry`, `last_update_check_at`). Returns `{}` when the file is absent.
 *
 * Throws `CloudGridError` with a human-readable message when the file exists
 * but is not parseable YAML (or parses to a non-mapping) — callers that must
 * not silently proceed (login, doctor) surface this and exit non-zero.
 */
function readRawConfig(): Record<string, unknown> {
  const configPath = join(getCloudGridHome(), 'config.yaml');
  if (!existsSync(configPath)) return {};
  let parsed: unknown;
  try {
    parsed = parseYaml(readFileSync(configPath, 'utf-8'));
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    throw new CloudGridError(
      `~/.cloudgrid/config.yaml is not valid YAML and could not be read.\n` +
        `Fix the file or remove it, then run \`grid login\` again.\n  ${detail}`,
    );
  }
  if (parsed == null) return {};
  if (typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new CloudGridError(
      `~/.cloudgrid/config.yaml is malformed (expected a set of key: value pairs).\n` +
        `Fix the file or remove it, then run \`grid login\` again.`,
    );
  }
  return parsed as Record<string, unknown>;
}

/**
 * Load config for the sign-in flow, self-healing a missing or partial
 * `~/.cloudgrid/config.yaml`.
 *
 * Tamir QA-01 (2026-06-18): on a clean machine the startup update-check
 * (`update-check.ts` → `setLastUpdateCheck`) writes config.yaml carrying only
 * its own keys — so the file exists but has no `api_url`. `runLogin` then read
 * it back and crashed on `cfg.api_url.replace(...)` with
 * "Cannot read properties of undefined (reading 'replace')", blocking every
 * brand-new user at step one.
 *
 * This loader never returns a config without `api_url`: a missing or empty
 * value is seeded with the production default and persisted back, preserving
 * every other key already on disk. Only seeds — an existing `api_url` is left
 * untouched, so this does not change the endpoint for anyone already configured.
 */
export function ensureLoginConfig(): CloudGridConfig {
  const existing = readRawConfig();
  const defaults = detectConfig();
  const currentApiUrl =
    typeof existing.api_url === 'string' ? existing.api_url.trim() : '';

  const healed: CloudGridConfig = {
    ...(existing as Partial<CloudGridConfig>),
    api_url: currentApiUrl || (defaults.api_url ?? PROD_API_URL),
    registry: typeof existing.registry === 'string' ? existing.registry : '',
    domain:
      typeof existing.domain === 'string' && existing.domain.trim()
        ? (existing.domain as string)
        : (defaults.domain ?? 'cloudgrid.io'),
  };

  // Persist only when we actually seeded api_url, so an already-configured
  // machine takes no write and no endpoint change.
  if (!currentApiUrl) saveConfig(healed);
  return healed;
}

/**
 * Validate `~/.cloudgrid/config.yaml`'s shape and rewrite it with sensible
 * defaults, preserving any parseable keys. Backs `grid doctor --fix`.
 *
 * Unlike `ensureLoginConfig`, a malformed/unparseable file is not fatal here —
 * `--fix` is the repair verb, so it overwrites with defaults and reports that
 * it did. Returns the human-readable notes describing what changed.
 */
export function repairConfig(): { notes: string[]; config: CloudGridConfig } {
  const configPath = join(getCloudGridHome(), 'config.yaml');
  const notes: string[] = [];
  let existing: Record<string, unknown> = {};

  if (!existsSync(configPath)) {
    notes.push('config.yaml was missing — creating it with defaults.');
  } else {
    try {
      existing = readRawConfig();
    } catch {
      notes.push('config.yaml was not valid YAML — rewriting it with defaults.');
    }
  }

  const defaults = detectConfig();
  if (!(typeof existing.api_url === 'string' && existing.api_url.trim())) {
    existing.api_url = defaults.api_url ?? PROD_API_URL;
    notes.push(`Seeded api_url=${existing.api_url}`);
  }
  if (!(typeof existing.domain === 'string' && (existing.domain as string).trim())) {
    existing.domain = defaults.domain ?? 'cloudgrid.io';
    notes.push(`Seeded domain=${existing.domain}`);
  }
  if (existing.registry === undefined) existing.registry = '';

  const config = existing as unknown as CloudGridConfig;
  saveConfig(config);
  if (notes.length === 0) notes.push('config.yaml shape is valid — no changes needed.');
  return { notes, config };
}

export function ensureAppsDir() {
  mkdirSync(getAppsDir(), { recursive: true });
}

/**
 * Create an API client from the loaded config + credentials + active-org.
 *
 * Note: v0.8 commands should use `getV2Client()` from `./api/v2-client.js`
 * instead. `createApiClient` remains for the legacy commands (hooks,
 * feedback) that haven't been migrated yet; M9 will retire this.
 *
 * The JWT lives in `~/.cloudgrid/credentials` (a separate file with 0600
 * perms), not in `config.yaml`. The active org slug lives in `config.yaml`
 * (`active_org_slug`) and is sent as `X-CloudGrid-Org` so the API's
 * org-context middleware can pick the right org for users in 2+ orgs.
 *
 * Optional `orgSlugOverride` lets a command (e.g. `grid deploy --org`)
 * override the active org for the duration of one invocation without
 * mutating the persisted config.
 */
export function createApiClient(
  config?: CloudGridConfig,
  orgSlugOverride?: string | null,
): CloudGridApiClient {
  const cfg = config || loadConfig();
  if (!cfg.api_url) throw new Error('api_url not set in ~/.cloudgrid/config.yaml.');
  const creds = readCredentials();
  if (!creds?.jwt) throw new Error('Not authenticated. Run: grid login (or set CLOUDGRID_TOKEN for non-interactive use).');
  const orgSlug = orgSlugOverride ?? getActiveOrgSlug();
  return new CloudGridApiClient(cfg.api_url, creds.jwt, orgSlug);
}
